import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import { ChartType } from '@/features/charts/components/create-chart-type-selection';
import { useDocumentsHubFilterStore } from '@/features/documents/components/pages/documents-hub-page';
import { useForecastsFilterStore } from '@/features/forecasts/hooks/use-forecasts-hub-filter';
import { useForecastsHubStore } from '@/features/forecasts/hooks/use-forecasts-hub-store';
import { useInsightsFilterStore } from '@/features/insights/store/use-insights-hub-filter';
import { useInsightsHubChartsStore } from '@/features/insights/store/use-insights-hub-filter-store';
import { useInsightsKpiStore } from '@/features/insights/store/use-insights-kpi-store';
import { useNotificationHubFilterStore } from '@/features/notifications/components/notification-hub-filter';
import { useOrgMemberFilterStore } from '@/features/org/components/pages/org-member-management/use-org-member-filter-store';
import { useCurrentOrgId } from '@/features/org/hooks/use-current-org-id';
import { OrgId } from '@/features/org/types/org-id';
import { usePartnerHubFilterStore } from '@/features/partners/components/pages/partner-hub-page';
import { useSubprojectHubFilterStore } from '@/features/projects/subprojects-hub/hooks/subprojects-filter';
import { useProjectHubFilterStore } from '@/shared/hooks/use-hub-filter-store';

/** Query key prefixes that must survive an org switch (user-level data). */
const PRESERVED_QUERY_PREFIXES = ['authenticated-user', 'orgs', 'current-org'];

function isPreservedQuery(queryKey: readonly unknown[]): boolean {
  const first = queryKey[0];
  return typeof first === 'string' && PRESERVED_QUERY_PREFIXES.includes(first);
}

/**
 * Resets all org-scoped React Query cache and Zustand stores whenever the
 * active organisation changes. Must be mounted inside `QueryClientProvider`.
 */
export function useResetOnOrgChange() {
  const currentOrgId = useCurrentOrgId();
  const queryClient = useQueryClient();
  const previousOrgIdRef = useRef<OrgId | null>(currentOrgId);

  useEffect(() => {
    const previousOrgId = previousOrgIdRef.current;
    previousOrgIdRef.current = currentOrgId;

    // Only reset when switching from one org to another (not on initial mount)
    if (!previousOrgId || previousOrgId === currentOrgId) return;

    // 1. Remove all org-scoped queries from the cache
    queryClient.removeQueries({
      predicate: (query) => !isPreservedQuery(query.queryKey),
    });

    // 2. Reset all hub filter stores
    useProjectHubFilterStore.getState().reset();
    useDocumentsHubFilterStore.getState().reset();
    useInsightsFilterStore.getState().reset();
    useForecastsFilterStore.getState().reset();
    usePartnerHubFilterStore.getState().reset();
    useSubprojectHubFilterStore.getState().reset();
    useOrgMemberFilterStore.getState().reset();
    useNotificationHubFilterStore.getState().reset();

    // 3. Reset KPI / chart selection stores
    useInsightsKpiStore.getState().setUniqueKpiId(undefined);
    useForecastsHubStore.getState().setUniqueKpiId(undefined);
    useInsightsHubChartsStore.setState({
      timelineChartType: ChartType.LINE,
      accumulatedChartType: ChartType.PIE,
      timelineChartTypes: [],
      accumulatedChartTypes: [],
    });
  }, [currentOrgId, queryClient]);
}
